// GOLDEN PATH CODE
// An example of mutations that update the local data that is displayed on the website
// Based on

// Hooks
import {
  Mutation,
  QueryClient,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useFeatures, getEnabledFeatures } from '../../hooks/features';
import { getClient } from '../../utils/getClient';
// Types
import {
  Event,
  Shift,
  Attendance,
  BulkCreateAttendanceRequest,
  BulkCreateAttendanceResponse,
  BulkUpdateAttendanceRequest,
  BulkUpdateAttendanceResponse,
  CreateAttendanceCommandInput,
  CreateAttendanceCommandOutput,
  CreateAttendanceRequest,
  GetAttendancesByQueryParamCommandOutput,
  GetDetailedSignupsByQueryParamCommandOutput,
  Person,
  RedVelvetApi,
  ShiftType,
} from '@amzn/red-velvet-api';

import { callQueryClientAllPagesRedVelvetQuery } from '../allPagesRedVelvetQuery';

const MUTATION_PREFIX = ['RedVelvetApi', 'timeTracking'];

function getAttendanceForShiftFromDetailedSignups(
  queryClient: QueryClient,
  shiftId: string,
  alias: string
) {
  const detailedSignups = queryClient.getQueriesData<GetDetailedSignupsByQueryParamCommandOutput>({
    queryKey: ['RedVelvetApi', 'getDetailedSignupsByQueryParam'],
  });
  const signup = detailedSignups
    .flatMap(([key, response]) => response?.signups || [])
    .find(
      (detailedSignup) =>
        detailedSignup.shift.shiftId === shiftId &&
        detailedSignup.signupDetails.attendance?.alias === alias
    );
  if (signup?.signupDetails.attendance) {
    return signup.signupDetails.attendance;
  }

  return undefined;
}

async function getAttendanceForShift(
  queryClient: QueryClient,
  eventId: string,
  shiftId: string,
  userAlias: string,
  featureFlags: string[]
) {
  const attendances = await callQueryClientAllPagesRedVelvetQuery(
    queryClient,
    {
      redVelvetQueryKey: ['getAttendancesByQueryParam', { eventId, userAlias }],
      converter: (output: GetAttendancesByQueryParamCommandOutput) => ({
        items: output.attendances || [],
        next: output.page,
      }),
    },
    featureFlags
  );
  return attendances.find((attendance) => attendance.shiftId === shiftId);
}

export function generateAttendanceKey(eventId: string, shiftId: string, alias: string) {
  return MUTATION_PREFIX.concat(['attendance', eventId, shiftId, alias]);
}

// GOLDEN PATH NOTE
// This function serves as a way to get the specific attendance for a shift by user id
// It should be used as the source of truth for any attendance, as it is immediately updated by the
// mutation instead of requiring a refetch from the API
export function useAttendance(eventId: string, shiftId: string, alias: string) {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  return useQuery({
    queryKey: generateAttendanceKey(eventId, shiftId, alias),
    queryFn: async () =>
      getAttendanceForShiftFromDetailedSignups(queryClient, shiftId, alias) ||
      (await getAttendanceForShift(
        queryClient,
        eventId,
        shiftId,
        alias,
        getEnabledFeatures(features, searchParams)
      )),
  });
}

interface BulkAttendanceOperationResult {
  updatedAttendances: BulkCreateAttendanceResponse;
  createdAttendances: BulkUpdateAttendanceResponse;
}

async function updateAttendanceForShiftAndUserInBulk(
  client: RedVelvetApi,
  eventId: string,
  shiftId: string,
  existingAttendances?: Attendance[],
  newAttendances?: CreateAttendanceRequest[]
): Promise<BulkAttendanceOperationResult> {
  // Separate into updates and creates
  const toUpdate: BulkUpdateAttendanceRequest = { updateRequests: [] };
  const toCreate: BulkCreateAttendanceRequest = { attendanceRequests: [] };

  existingAttendances?.forEach((input) => {
    toUpdate.updateRequests.push({
      attendanceId: input.attendanceId,
      minutes: input.minutes,
      attendanceStatus: input.attendanceStatus,
    });
  });

  newAttendances?.forEach((input) => {
    toCreate.attendanceRequests.push({
      ...input,
      eventId,
      shiftId,
    });
  });

  // Execute bulk operations
  const updateResult =
    toUpdate.updateRequests.length > 0
      ? await client.bulkUpdateAttendance({ updateRequests: toUpdate.updateRequests })
      : { attendanceIds: [] };

  const createResult =
    toCreate.attendanceRequests.length > 0
      ? await client.bulkCreateAttendance({ attendanceRequests: toCreate.attendanceRequests })
      : { attendanceIds: [] };

  return {
    updatedAttendances: { attendanceIds: updateResult.attendanceIds },
    createdAttendances: { attendanceIds: createResult.attendanceIds },
  };
}

function getNewParticipantData(
  queryClient: QueryClient,
  updatingUser: string,
  data: (CreateAttendanceCommandOutput & CreateAttendanceCommandInput)[]
) {
  const peopleData = queryClient.getQueriesData<{ people: Person[] }>({
    queryKey: ['RedVelvetApi', 'getPeople'],
  });

  return data.map((attendance) => {
    const [, singlePeopleQueryData] = peopleData.find(
      ([queryKey, data]) =>
        data?.people && data.people.find((person) => person?.alias === attendance.alias)
    ) || ['', { people: [] }];
    const person =
      singlePeopleQueryData?.people &&
      singlePeopleQueryData.people.find((person) => person.alias === attendance.alias);

    return {
      volunteerAlias: attendance.alias,
      volunteerFirstName: person?.firstName,
      volunteerLastName: person?.lastName,
      volunteerBuildingName: person?.building,
      volunteerManagerAlias: person?.managerLogin,
      isSignedUp: false,
      attendanceUpdatedByAlias: updatingUser,
      attendanceStatus: attendance.attendanceStatus,
      attendanceMinutes: attendance.minutes,
      attendanceId: attendance.attendanceId,
    };
  });
}

type ShiftAttendanceUpdateData = {
  event: Event;
  shift: Shift;
  attendanceInputs: BulkCreateAttendanceRequest;
};

export function useShiftAttendance(updatingUser: string, pageSize = 25) {
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const client = getClient(getEnabledFeatures(features, searchParams));
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: MUTATION_PREFIX.concat(['createShiftAttendance']),
    mutationFn: async ({ event, shift, attendanceInputs }: ShiftAttendanceUpdateData) => {
      // First, create a new event and/or shift or update the event and shift data
      const newShiftData = {
        event,
        ...shift,
        shiftLength: shift.shiftLength || 0,
        startDateTime: shift.startDateTime || new Date(Date.now()),
        eventId: event.eventId || undefined,
      };

      let result: { eventId: string; shiftId: string };
      if (!shift.shiftId) {
        // Only assign UNLISTED_EVENT_SHIFT type when creating new shifts.
        // This ensures GiveHub events with REGULAR shifts maintain their original type when updated.
        // The shiftType assignment must happen here (not earlier) to avoid affecting updateShiftAndEvent calls.
        const unlistedEventShiftData = {
          ...newShiftData,
          shiftType: ShiftType.UNLISTED_EVENT_SHIFT,
        };
        result = await client.createShiftAndEvent(unlistedEventShiftData);
      } else {
        const updatedShiftData = {
          ...newShiftData,
          event,
          shiftId: shift.shiftId,
        };
        result = await client.updateShiftAndEvent(updatedShiftData);
      }

      // Set event by id (this will enable fetching updated event info easily)
      queryClient.setQueryData(['RedVelvetApi', 'getEventById', { eventId: result.eventId }], {
        ...event,
        eventId: result.eventId,
      });

      const eventId = result.eventId;
      const shiftId = result.shiftId;

      // Get existing attendances to determine which need update vs create
      const attendanceResults = await Promise.all(
        attendanceInputs.attendanceRequests.map(async (input) => {
          const existing = await getAttendanceForShift(
            queryClient,
            eventId,
            shiftId,
            input.alias,
            getEnabledFeatures(features, searchParams)
          );
          return { input, existing };
        })
      );

      const existingAttendances = attendanceResults
        .filter((result) => result.existing !== undefined)
        .map((result) => ({
          ...(result.existing as Attendance),
          minutes: result.input.minutes,
          attendanceStatus: result.input.attendanceStatus,
        }));

      const newAttendances = attendanceResults
        .filter((result) => !result.existing)
        .map((result) => result.input);

      const results = await updateAttendanceForShiftAndUserInBulk(
        client,
        eventId,
        shiftId,
        existingAttendances,
        newAttendances
      );

      let createIndex = 0;

      const attendances = attendanceInputs.attendanceRequests.map((input, index) => {
        const attendance = existingAttendances[index];
        let attendanceId = '';

        if (attendance) {
          attendanceId = attendance.attendanceId;
        } else {
          attendanceId = results.createdAttendances.attendanceIds[createIndex] || '';
          createIndex++;
        }

        return {
          ...input,
          attendanceId,
          shiftId,
          eventId,
          $metadata: {},
        };
      });

      return {
        event: {
          ...event,
          eventId,
        },
        shift: {
          ...shift,
          shiftId,
          eventId,
        },
        attendances,
        created: !shift.shiftId,
      };
    },
    onSuccess: (data) => {
      for (let page = 1; (page - 1) * pageSize < data.attendances.length; page++) {
        const pageOfData = data.attendances.slice((page - 1) * pageSize, page * pageSize);
        const queryKey = [
          'RedVelvetApi',
          'getParticipationsByEventShift',
          { eventId: data.event.eventId, shiftId: data.shift.shiftId, pageSize },
          page,
        ];
        const newData = getNewParticipantData(queryClient, updatingUser, pageOfData);
        queryClient.setQueryData(queryKey, newData);
      }
    },
  });
}

export function useTrackTime(eventId: string, shiftId: string) {
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const client = getClient(getEnabledFeatures(features, searchParams));
  const queryClient = useQueryClient();

  return useMutation<CreateAttendanceCommandOutput, Error, CreateAttendanceCommandInput>({
    mutationKey: MUTATION_PREFIX.concat([shiftId, 'createAttendance']),
    mutationFn: async (attendanceInput) => {
      const attendance = queryClient.getQueryData<Attendance | undefined>(
        generateAttendanceKey(eventId, shiftId, attendanceInput.alias)
      );

      if (attendance) {
        return await client.updateAttendance({ ...attendance, ...attendanceInput });
      }
      return await client.createAttendance({ ...attendanceInput, shiftId });
    },
    // GOLDEN PATH NOTE
    // On a successful mutation, the data in the query client is replaced with the new values immediately
    // This triggers a UI rerender that does not require an extra loading spinner, since the new data is already
    // avalible (ie we can infer it from the user's input)
    onSuccess: (data, attendanceInput) => {
      const oldAttenance = queryClient.getQueryData<Attendance | undefined>(
        generateAttendanceKey(eventId, shiftId, attendanceInput.alias)
      );
      queryClient.setQueryData(generateAttendanceKey(eventId, shiftId, attendanceInput.alias), {
        ...oldAttenance,
        ...attendanceInput,
        attendanceId: data.attendanceId,
      });
      // The VolunteerEventShiftsTable derives "Your status" + the row action from the
      // getAttendancesByQueryParam list (not generateAttendanceKey), so invalidate it here the same
      // way useCreateSignup invalidates getSignupsByQueryParam — otherwise the row stays stale until a
      // page refresh. The setQueryData above is kept for the carousel / My Activity useAttendance reader.
      queryClient.invalidateQueries({
        queryKey: ['RedVelvetApi', 'getAttendancesByQueryParam'],
      });
      // My Activity's event-grouped tabs (VolunteerEventsTab) read attendance from the drained
      // getDetailedSignupsByQueryParam list, not getAttendancesByQueryParam, so invalidate it too —
      // otherwise their Status column stays stale after an edit even though the reopened modal (which
      // reads the single-attendance cache set above) shows the new value. Mirrors the signup hooks.
      queryClient.invalidateQueries({
        queryKey: ['RedVelvetApi', 'getDetailedSignupsByQueryParam'],
      });
    },
  });
}

function attendanceMutationData(mutation: Mutation) {
  return {
    status: mutation.state.status,
    // GOLDEN PATH NOTE
    // This cast is safe because we are filtering on the mutation key
    // However react-query has no way to know the input shape
    variables: mutation.state.variables as CreateAttendanceCommandInput,
    error: mutation.state.error,
    timestamp: mutation.state.submittedAt,
    data: mutation.state.data as CreateAttendanceCommandOutput | null,
  };
}

function generateAttendanceAccordingToInputAndOutput(
  existedAttendance?: Attendance,
  input?: CreateAttendanceCommandInput,
  output?: CreateAttendanceCommandOutput,
  submittedTime?: number
): Partial<Attendance> | undefined {
  if (!input || !output || !submittedTime) {
    return existedAttendance;
  }
  const timestamp = new Date(submittedTime);

  // for attendance update case
  if (existedAttendance) {
    return {
      ...existedAttendance,
      minutes: input.minutes,
      attendanceStatus: input.attendanceStatus,
      updatedTime: timestamp,
    };
  }
  // for attendance create case
  return {
    ...input,
    attendanceId: output.attendanceId,
    reportedTime: timestamp,
    updatedTime: timestamp,
  };
}

export function useLatestAttendanceState(
  shiftId: string,
  attendance?: Attendance
): Partial<Attendance> | undefined {
  const data = useMutationState({
    filters: {
      mutationKey: MUTATION_PREFIX.concat([shiftId, 'createAttendance']),
    },
    select: attendanceMutationData,
  });
  let lastIndex = data.length - 1;
  while (lastIndex >= 0) {
    const lastUpdate = data[lastIndex];
    if (lastUpdate.status === 'success') {
      return generateAttendanceAccordingToInputAndOutput(
        attendance,
        lastUpdate.variables,
        lastUpdate.data as CreateAttendanceCommandOutput,
        lastUpdate.timestamp
      );
    }
    lastIndex--;
  }
  return attendance;
}
